import React, { useState, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import Image from "aldoo-ra/Image"
import { PaperAirplaneIcon, HandRaisedIcon } from "@heroicons/react/24/outline"

// Hold duration before a press becomes a "send folders" gesture. The circular
// progress ring fills over this period; on completion folders open without the
// user having to release. Desktop (mouse) only — see beginHold.
const HOLD_MS = 650

// Progress ring geometry (sized to wrap the 140px button).
const RADIUS = 92
const CIRC = 2 * Math.PI * RADIUS

export default function UploadLanding({ texts, creating, onPickFiles, onPickFolders, onDrop }) {
  const [progress, setProgress] = useState(0) // 0..1 hold progress
  const [holding, setHolding] = useState(false)
  const [pressed, setPressed] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [msgIdx, setMsgIdx] = useState(0)
  const [isFine, setIsFine] = useState(false)

  const rafRef = useRef(null)
  const startRef = useRef(0)
  const consumedRef = useRef(false) // hold completed → folders already opened
  const downRef = useRef(false)     // pointer is down on this element
  const dragDepthRef = useRef(0)

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return
    setIsFine(window.matchMedia("(pointer: fine)").matches)
  }, [])

  const messages = [
    isFine
      ? texts?.clickToSendFiles
      : (texts?.tapToSendFiles || texts?.clickToSendFiles),
    texts?.holdToSendFolders,
  ]
  const dragLabel = texts?.dragFilesHere

  // Alternate the bubble message (only when there is more than one).
  useEffect(() => {
    if (messages.length < 2) return
    const t = setInterval(() => setMsgIdx((i) => (i + 1) % messages.length), 3000)
    return () => clearInterval(t)
  }, [messages.length])

  // Full-screen landing: lock page scroll while it is shown.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = prev }
  }, [])

  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
  }

  const tick = useCallback(() => {
    const t = Math.min(1, (performance.now() - startRef.current) / HOLD_MS)
    setProgress(t)
    if (t >= 1) {
      consumedRef.current = true
      setHolding(false)
      setProgress(0)
      stopRaf()
      onPickFolders?.()
      return
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [onPickFolders])

  const beginHold = (e) => {
    if (creating) return
    if (e.button != null && e.button !== 0) return // left button only
    downRef.current = true
    consumedRef.current = false
    setPressed(true)
    startRef.current = performance.now()
    setHolding(true)
    stopRaf()
    rafRef.current = requestAnimationFrame(tick)
  }

  // Pointer released on the element → complete the gesture. Quick release
  // (or no armed hold) opens files; a completed hold already opened folders.
  // Guarded by downRef so it fires once per gesture (pointerup only, never
  // the leave/cancel path that fires when the file dialog steals focus).
  const releaseHold = () => {
    if (!downRef.current) return
    downRef.current = false
    const wasHolding = holding
    const elapsed = performance.now() - startRef.current
    stopRaf()
    setHolding(false)
    setProgress(0)
    setPressed(false)
    if (consumedRef.current || creating) return
    if (!wasHolding || elapsed < HOLD_MS) onPickFiles?.()
  }

  // Pointer left the element or was cancelled → abort the gesture without
  // opening anything. Prevents the open-loop when the file dialog opens.
  const cancelHold = () => {
    downRef.current = false
    stopRaf()
    setHolding(false)
    setProgress(0)
    setPressed(false)
  }

  useEffect(() => () => stopRaf(), [])

  // Drag overlay — depth counter so nested dragenter/leave don't flicker.
  const onDragEnter = (e) => { e.preventDefault(); dragDepthRef.current += 1; setDragging(true) }
  const onDragOver = (e) => { e.preventDefault() }
  const onDragLeave = (e) => {
    e.preventDefault()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
    if (dragDepthRef.current === 0) setDragging(false)
  }
  const handleDrop = (e) => {
    dragDepthRef.current = 0
    setDragging(false)
    onDrop?.(e)
  }

  return (
    <div
      className="relative w-full h-[100dvh] -mt-16 flex items-center justify-center overflow-hidden select-none cursor-pointer"
      onPointerDown={beginHold}
      onPointerUp={releaseHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
      style={{ touchAction: "none" }}
    >
      {/* Semantic heading for SEO/screen readers. Visually hidden — the page's
          visual hierarchy is carried by the button + speech bubble above. */}
      <h1 className="sr-only">
        {texts?.seoHeading}
      </h1>
      <h2 className="sr-only">
        {texts?.seoSubheading}
      </h2>

      {/* Local keyframes so the ripple works regardless of the Tailwind build. */}
      <style>{`
        @keyframes uploadRipple {
          0%   { transform: scale(1);   opacity: 0.5; }
          80%  { opacity: 0.07; }
          100% { transform: scale(3.4); opacity: 0; }
        }
      `}</style>

      {/* Center column: comic bubble above, then the button with ripples.
          Flex layout keeps the bubble centered over the button in every build. */}
      <div className="relative flex flex-col items-center">
        {/* Comic-style speech bubble with a downward pointer */}
        <div className="-mb-5 animate-soft-bounce pointer-events-none relative z-20">
          <div className="relative rounded-2xl bg-paper shadow-lg px-5 py-3 text-center">
            <span className="block text-sm font-medium text-text whitespace-nowrap">
              {messages[msgIdx]}
            </span>
            {/* Pointing edge */}
            <span className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3.5 h-3.5 bg-paper rotate-45 shadow-lg" />
          </div>
        </div>

        {/* Button + ripples + hold-progress ring */}
        <div className="relative flex items-center justify-center w-[260px] h-[260px]">
          {/* Pulsating sonar rings (fromsmash-style): white alpha circles that
              start at the button size and expand outward while fading. */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="absolute w-[140px] h-[140px] rounded-full bg-white/40"
                style={{ animation: `uploadRipple 3.4s ease-out ${i * 0.85}s infinite`, transformOrigin: "center" }}
              />
            ))}
          </div>

          {/* Hold-progress ring */}
          <svg
            className="absolute w-[210px] h-[210px] -rotate-90 pointer-events-none"
            viewBox="0 0 210 210"
            style={{ opacity: holding ? 1 : 0, transition: "opacity 0.15s" }}
          >
            <circle cx="105" cy="105" r={RADIUS} fill="none" stroke="white" strokeOpacity="0.35" strokeWidth="6" />
            <circle
              cx="105" cy="105" r={RADIUS} fill="none" stroke="white" strokeWidth="6" strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={CIRC * (1 - progress)}
            />
          </svg>

          {/* The button */}
          <div
            className={`relative w-[140px] h-[140px] bg-theme_white shadow-xl rounded-full flex items-center justify-center transition-transform duration-150 ${pressed ? "scale-95" : "scale-100"}`}
          >
            {/* <PaperAirplaneIcon className="w-16 h-16 text-primary -rotate-45 translate-x-1" /> */}

            <Image
              id="aldoo_transfer_logo_icon"
              className="w-16 h-16 object-contain"
            />
          </div>
        </div>
      </div>

      {/* Bottom hint with hand-drop gesture */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex items-center gap-2 text-text text-sm pointer-events-none">
        <HandRaisedIcon className="w-5 h-5" />
        <span>{dragLabel}</span>
      </div>

      {/* Drag-over overlay — portalled to body so transformed ancestors
          (MainBackground, etc) can't trap the fixed positioning or z-index
          below the navbar. Paper chip behind label keeps text readable. */}
      {dragging && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[2147483646] flex flex-col items-center justify-center gap-4 bg-canvas/70 backdrop-blur-sm border-4 border-dashed border-primary/60">
          <div className="flex flex-col items-center gap-3 bg-paper/95 border border-bordered rounded-3xl shadow-xl px-8 py-6 max-w-[90vw]">
            <HandRaisedIcon className="w-14 h-14 text-primary" />
            <span className="text-lg font-semibold text-text text-center">{dragLabel}</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
